import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { useFetch } from '@/hooks/useFetch';
import type { AgentListData, GetAgentsListResponse } from '@/types/clientDetailsApiTypes';

const AGENTS_LIST_QUERY_KEY = 'agents-list';
const PER_PAGE = 15;

export const useGetAgentsListQuery = ({
  minSearchLength = 2,
  enabled = true,
}: { minSearchLength?: number; enabled?: boolean } = {}) => {
  const { doFetch } = useFetch<void, GetAgentsListResponse>();
  const [search, setSearch] = useState('');

  const queryResult = useInfiniteQuery({
    queryKey: [AGENTS_LIST_QUERY_KEY, search],
    queryFn: async ({ pageParam = 1 }: { pageParam?: number }) => {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      params.append('per_page', String(PER_PAGE));
      params.append('page', String(pageParam));

      const response = await doFetch(`/api/agents/list?${params.toString()}`);

      return {
        data: response.data,
        pagination: response.pagination,
        nextCursor: response.pagination?.current_page !== response.pagination?.last_page ? pageParam + 1 : undefined,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: 1,
    enabled: enabled && search.length >= minSearchLength,
  });

  const toOption = useCallback(
    (agent: AgentListData): SelectorOption<string> => ({
      label: agent.full_name,
      value: String(agent.id),
      image: agent.image_relative_url ?? undefined,
    }),
    []
  );

  // All accumulated options across all loaded pages for the current search
  const options = useMemo(
    () => (queryResult.data?.pages.flatMap((page) => page.data) ?? []).map(toOption),
    [queryResult.data, toOption]
  );

  const onMenuScrollToBottom = useCallback(() => {
    if (queryResult.hasNextPage && !queryResult.isFetchingNextPage) {
      queryResult.fetchNextPage();
    }
  }, [queryResult]);

  return {
    options,
    search,
    setSearch,
    onMenuScrollToBottom,
    isLoading: queryResult.isFetching,
    isFetchingNextPage: queryResult.isFetchingNextPage,
  };
};
